import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import DocumentVerificationModal from "./DocumentVerificationModal";
import WishToaster from "./WishToaster";
import useMasters from "../services/useMasters";

const SwalAlert = withReactContent(Swal);

const DocumentUploadGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loggedInUser } = useMasters();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkDocumentStatus = () => {
      const distributorData = localStorage.getItem("distributor");
      if (!distributorData) return true; // Allow if no distributor data

      try {
        const distributor = JSON.parse(distributorData);
        const docFlags = distributor?.dist_document_flag;

        if (!docFlags) return true; // Allow if no document flags

        // Check if any document flag is 1 and skip_flag is also 1
        const hasSkippedDocuments = docFlags.skip_flag === 1 && 
          Object.entries(docFlags).some(([key, value]) => {
            return key !== 'skip_flag' && value === 0;
          });

        return !hasSkippedDocuments; // Block if has skipped documents
      } catch (error) {
        console.error("Error checking document status:", error);
        return true; // Allow on error
      }
    };

    const shouldAllowAccess = checkDocumentStatus();
    setIsLoading(false);

    // If access should be blocked and not already on dashboard
    if (!shouldAllowAccess && location.pathname !== "/") {
      WishToaster({
        toastType: "error",
        toastMessage: "Please complete your document verification to continue.",
      });

      // Show document verification modal
      SwalAlert.fire({
        html: (
          <DocumentVerificationModal
            documentFlags={JSON.parse(localStorage.getItem("distributor"))?.dist_document_flag}
            onUpload={async (documentType, file) => {
              // Handle document upload
              try {
                // Your document upload logic here
                // After successful upload:
                const newDistributorData = JSON.parse(localStorage.getItem("distributor"));
                newDistributorData.dist_document_flag[documentType] = 1;
                localStorage.setItem("distributor", JSON.stringify(newDistributorData));
                
                // Check if all documents are complete
                const allComplete = Object.entries(newDistributorData.dist_document_flag)
                  .every(([key, value]) => key === 'skip_flag' || value === 1);
                
                if (allComplete) {
                  Swal.close();
                  WishToaster({
                    toastType: "success",
                    toastMessage: "All documents uploaded successfully!",
                  });
                }
              } catch (error) {
                console.error("Error uploading document:", error);
                WishToaster({
                  toastType: "error",
                  toastMessage: "Failed to upload document. Please try again.",
                });
              }
            }}
            onSkip={() => {
              // Disable skip functionality
              WishToaster({
                toastType: "error",
                toastMessage: "Document upload is mandatory. You cannot skip at this time.",
              });
            }}
          />
        ),
        showConfirmButton: false,
        showCancelButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        width: "90%",
      });

      // Redirect to dashboard
      navigate("/");
      return;
    }
  }, [navigate, location.pathname, loggedInUser]);

  if (isLoading) {
    return null; // Or your loading component
  }

  return children;
};

export default DocumentUploadGuard;
